import graphQLClient from 'config/graphql-instance';
import { ClientError, gql } from 'graphql-request';
import { z } from 'zod';

export type InferenceResponse = {
  metadata: {
    [key:string]: {
      page_label: string,
      file_name: string,
      file_path: string,
      file_type: string,
      file_size: number,
      creation_date: string,
      last_modified_date: string,
      last_accessed_date: string,
      text: string
    }
  },
  question: string,
  response: string
}

export type GQLGetInference = {
  getInference: InferenceResponse
}

export const getInference = async (payload: { question: string }) => {
  const query = gql`
    query GetInference($question: String!) {
      getInference(question: $question) {
        metadata
        question
        response
      }
    }
  `;

  const response = await graphQLClient().request(query, payload);
  return response;
};

export type PromptTranslation = {
  english: string;
  spanish: string;
};

// Frontend reads .english only — .spanish fields are present in the GraphQL response
// but never consumed by new frontend code (Task 7 removed spanish consumption).
export type GQLPrompt = {
  readonly _id: string;
  readonly name: string;
  readonly model: string;
  readonly english: { readonly description: string; readonly content: string };
  readonly spanish: { readonly description: string; readonly content: string };
};

// AiOptions prompt translations follow the same pattern — .english is the active locale.
export type AiOptions = {
  model_agent: string;
  model_vision: string;
  prompt_system: PromptTranslation;
  prompt_screen: PromptTranslation;
  prompt_image: PromptTranslation;
};

export type AiOptionsWithPrompts = AiOptions & {
  readonly prompts: GQLPrompt[];
};

export type GQLGetAiOptions = {
  getAiOptions: AiOptions;
};

export type GQLGetAiOptionsWithPrompts = {
  readonly getAiOptions: AiOptionsWithPrompts;
};

const promptTranslationSchema = z.object({ english: z.string(), spanish: z.string().optional() }).transform(({ english, spanish }) => ({ english, spanish: spanish ?? '' }));

const partialAiOptionsSchema = z.object({
  getAiOptions: z.object({
    model_agent: z.string(),
    model_vision: z.string(),
    prompt_system: promptTranslationSchema,
    prompt_screen: promptTranslationSchema,
    prompt_image: promptTranslationSchema,
  }),
}).transform(({ getAiOptions: options }) => options).nullable().catch(null);

let aiOptionsWithPromptsRequest: Promise<GQLGetAiOptionsWithPrompts> | null = null;

export class AiOptionsWithPromptsError extends Error {
  readonly name = 'AiOptionsWithPromptsError';

  constructor(
    readonly aiOptions: AiOptions | null,
    readonly originalError: unknown,
  ) {
    super(originalError instanceof Error ? originalError.message : 'Failed to load shared prompts');
  }
}

export const getAiOptions = async () => {
  const query = gql`
    query GetAiOptions {
      getAiOptions {
        model_agent
        model_vision
        prompt_system {
          english
        }
        prompt_screen {
          english
        }
        prompt_image {
          english
        }
      }
    }
  `;

  const response = await graphQLClient().request<GQLGetAiOptions>(query);
  return response;
};

export const getAiOptionsWithPrompts = (): Promise<GQLGetAiOptionsWithPrompts> => {
  if (aiOptionsWithPromptsRequest !== null) return aiOptionsWithPromptsRequest;

  const query = gql`
    query GetAiOptionsWithPrompts {
      getAiOptions {
        model_agent
        model_vision
        prompt_system {
          english
        }
        prompt_screen {
          english
        }
        prompt_image {
          english
        }
        prompts {
          _id
          name
          model
          english { description, content }
        }
      }
    }
  `;

  const request = graphQLClient()
    .request<GQLGetAiOptionsWithPrompts>(query)
    .catch((error: unknown) => {
      const partialAiOptions = error instanceof ClientError
        ? partialAiOptionsSchema.parse(error.response.data)
        : null;

      throw new AiOptionsWithPromptsError(partialAiOptions, error);
    })
    .finally(() => {
      aiOptionsWithPromptsRequest = null;
    });

  aiOptionsWithPromptsRequest = request;
  return request;
};

export const getPrompts = async () => {
  const query = gql`
    query GetPrompts {
      getAiOptions {
        prompts {
          _id
          name
          model
          english { description, content }
        }
      }
    }
  `;

  const response = await graphQLClient().request<{ getAiOptions: { prompts: GQLPrompt[] } }>(query);
  return response;
};
